import { useEffect, useRef, type ReactNode } from 'react';
import { CalendarBlankIcon } from '@phosphor-icons/react';
import { filterChipVariants } from '@voll-intelligence/ui-kit/ui/filter-chip';
import { cn } from '@voll-intelligence/ui-kit/utils';
import {
  formatDayMonth,
  formatWeekdayDayMonth,
  parseDateOnly,
  toDateOnly,
} from '@voll-intelligence/dates';

/** Quantos dias a faixa mostra (frame 04 do "Conversacional → Bus"). */
const STRIP_SIZE = 5;

export type BusDayStripBounds = { readonly min?: string; readonly max?: string };

const shift = (day: Date, by: number): Date =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate() + by);

/**
 * Os dias da faixa (`YYYY-MM-DD`): o buscado no meio, dois de cada lado. A
 * janela nunca começa antes de hoje nem do `min`, e nunca passa do `max` — ela
 * desliza em vez de mostrar um dia que não se pode buscar, e encolhe quando o
 * intervalo inteiro tem menos de cinco dias. Vazio se `day` é ilegível.
 */
export function busDayWindow(day: string, today: string, bounds: BusDayStripBounds = {}): string[] {
  const picked = parseDateOnly(day);
  const now = parseDateOnly(today);
  if (!picked || !now) return [];
  const min = parseDateOnly(bounds.min);
  const max = parseDateOnly(bounds.max);
  const floor = min && min > now ? min : now;
  let start = shift(picked, -Math.floor(STRIP_SIZE / 2));
  if (max && shift(start, STRIP_SIZE - 1) > max) start = shift(max, -(STRIP_SIZE - 1));
  if (start < floor) start = floor;
  const days: string[] = [];
  for (let i = 0; i < STRIP_SIZE; i += 1) {
    const candidate = shift(start, i);
    if (max && candidate > max) break;
    days.push(toDateOnly(candidate));
  }
  return days;
}

export type BusDayStripProps = {
  /** O dia buscado da perna em cena (`YYYY-MM-DD`). */
  day: string;
  /** Hoje, no fuso da pessoa: o piso da faixa. */
  today: string;
  bounds?: BusDayStripBounds;
  locale?: string;
  /** O nome acessível do grupo ("Buscar em outro dia"). */
  label: string;
  /** Uma busca em voo, ou o card congelado: os dias travam. */
  disabled?: boolean;
  onSelect(day: string): void;
};

/**
 * A faixa de dias da seleção curta de ônibus na conversa (frame 04): o dia
 * buscado em destaque e os vizinhos. Tocar um dia é pedir uma busca nova — a
 * faixa não filtra a lista, quem decide é o host.
 *
 * Cada dia é o chip de filtro do DS (pílula; o buscado com o fundo areia e o
 * contorno de tinta do frame, não o preenchimento preto do botão principal).
 * Os chips dividem a largura; quando não cabem (celular), a fileira rola na
 * horizontal sem barra, como os filtros da lista, em vez de cortar o rótulo.
 */
export function BusDayStrip({
  day,
  today,
  bounds,
  locale,
  label,
  disabled = false,
  onSelect,
}: Readonly<BusDayStripProps>): ReactNode {
  const days = busDayWindow(day, today, bounds);
  const now = parseDateOnly(today) ?? undefined;
  const rowRef = useRef<HTMLDivElement>(null);
  // Quando a fileira rola (celular), o dia buscado fica no centro dela. Só o
  // `scrollLeft` da própria fileira: `scrollIntoView` rolaria também a conversa.
  // O `ResizeObserver` mede na primeira entrega e de novo quando a caixa muda
  // (girar o aparelho, redimensionar): centraliza só quando a fileira PASSA a
  // rolar, para não desfazer a rolagem que a pessoa fez.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;
    let scrollable = false;
    const center = () => {
      const overflows = row.scrollWidth > row.clientWidth;
      const becameScrollable = overflows && !scrollable;
      scrollable = overflows;
      const current = row.querySelector<HTMLElement>(`[data-day="${day}"]`);
      if (!becameScrollable || !current) return;
      row.scrollLeft = current.offsetLeft - (row.clientWidth - current.offsetWidth) / 2;
    };
    const observer = new ResizeObserver(center);
    observer.observe(row);
    return () => {
      observer.disconnect();
    };
  }, [day]);
  if (days.length < 2) return null;
  return (
    <div
      ref={rowRef}
      role="group"
      aria-label={label}
      // `relative`: o `offsetLeft` dos chips passa a ser medido a partir da fileira.
      className="relative flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {days.map((candidate) => {
        const date = parseDateOnly(candidate);
        if (!date) return null;
        const current = candidate === day;
        return (
          <button
            key={candidate}
            data-day={candidate}
            type="button"
            className={cn(filterChipVariants(), 'flex-1')}
            aria-pressed={current}
            aria-label={formatWeekdayDayMonth(date, locale, now)}
            disabled={disabled}
            onClick={() => {
              if (!current) onSelect(candidate);
            }}
          >
            <CalendarBlankIcon aria-hidden="true" className="max-sm:hidden" />
            <span>{formatDayMonth(date, locale, now)}</span>
          </button>
        );
      })}
    </div>
  );
}
